import { useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { LayoutGroup, MotionConfig, motion } from 'framer-motion';
import { fadeIn, fadeInUp, staggerContainer, warmEase } from '@/shared/motion/motionPresets';
import { NavigatorAltsSection } from './NavigatorAltsSection';
import { NavigatorCareStageCard } from './NavigatorCareStageCard';
import { NavigatorCategoryTabs } from './NavigatorCategoryTabs';
import { NavigatorConciergeEmptyState } from './NavigatorConciergeEmptyState';
import { NavigatorProviderCard } from './NavigatorProviderCard';
import { NavigatorAltRow } from './NavigatorAltRow';
import { NavigatorSpecialtyIntro } from './NavigatorSpecialtyIntro';
import { NavigatorKeyResources } from './NavigatorKeyResources';
import { NavigatorAdditionalProviders } from './NavigatorAdditionalProviders';
import { splitCategories } from './splitCategories';
import type { NavigatorCategory, NavigatorProvider, NavigatorResultsViewModel } from './navigator-results.types';
import type { NavigatorResultsCopy } from './navigatorResultsCopy';

export type NavigatorProviderContext = {
  category: NavigatorCategory;
  index: number;
  isTopMatch: boolean;
  /** The provider currently featured as the plan's selection (feature 080). */
  isSelected: boolean;
  /** Feature 087 (ticket 15) — provider is the plan's global Best Match. */
  isBestMatch: boolean;
  /**
   * Promotes this provider to the specialty's selection (only set on alt rows
   * when the surface wires `onSelectProvider`) — custom renderers attach it
   * to their own Select control so the morph/pulse still runs.
   */
  onSelect?: () => void;
  /** A swap is still persisting — custom renderers disable their Select. */
  selectDisabled?: boolean;
};

type NavigatorResultsProps = {
  viewModel: NavigatorResultsViewModel;
  copy: NavigatorResultsCopy;
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  topSlot?: ReactNode;
  titleSlot?: ReactNode;
  headerActions?: ReactNode;
  footerSlot?: ReactNode;
  renderProviderActions?: (provider: NavigatorProvider, category: NavigatorCategory) => ReactNode;
  /** Full provider renderer override (e.g. FP rich cards with lazy loading). */
  renderProvider?: (provider: NavigatorProvider, context: NavigatorProviderContext) => ReactNode;
  /** Full key-resources renderer override (e.g. FP KeyResourcesPanel). */
  renderKeyResources?: (category: NavigatorCategory) => ReactNode;
  onAltsToggle?: (open: boolean, category: NavigatorCategory) => void;
  /**
   * Swap handler (feature 080): make this provider the specialty's single
   * selection. Omitted on read-only surfaces → alt rows stay view-only.
   * Returning a promise lets the results shell disable further Select
   * clicks until the swap settles (spam guard).
   */
  onSelectProvider?: (
    provider: NavigatorProvider,
    category: NavigatorCategory,
  ) => void | Promise<unknown>;
  /**
   * Wires the Care Concierge CTA on the empty-category state (a category with
   * no matched providers). Omitted on read-only surfaces → message only.
   */
  onConciergeRequest?: (category: NavigatorCategory) => void;
  /**
   * Matchmaking is still running, so the page is rendering ahead of the full
   * result set. Categories without providers are "not searched yet", not
   * "nothing found" — the distinction the concierge handoff depends on.
   */
  isMatching?: boolean;
  /**
   * Best Match override, keyed by provider id (087 ticket 10 prototype's
   * mock scenarios only — live providers set `isBestMatch` directly).
   */
  bestMatchProviderIds?: ReadonlySet<string>;
  /** Best Match highlight directly below care stage (feature 087, ticket 15). */
  beforeTabsSlot?: ReactNode;
};

/** Selection wins the featured slot; fall back to the AI top match (rank 1). */
const featuredProviderFor = (providers: NavigatorProvider[]) =>
  providers.find((provider) => provider.isSelected) ?? providers[0];

/** Sage ring pulse confirming a just-promoted selection (self-clearing). */
const swapPulse = {
  boxShadow: ['0 0 0 0px rgba(2,104,87,0.30)', '0 0 0 7px rgba(2,104,87,0)'],
};

export const NavigatorResults = ({
  viewModel,
  copy,
  activeIndex,
  onActiveIndexChange,
  topSlot,
  titleSlot,
  headerActions,
  footerSlot,
  renderProviderActions,
  renderProvider,
  renderKeyResources,
  onAltsToggle,
  onSelectProvider,
  onConciergeRequest,
  isMatching = false,
  bestMatchProviderIds,
  beforeTabsSlot,
}: NavigatorResultsProps) => {
  // Best Match (feature 087, ticket 15): the live API sets `provider.isBestMatch`
  // directly. `bestMatchProviderIds` stays as a fallback for the ticket-10
  // prototype's mock scenarios, whose fixture providers don't carry the field.
  const isBestMatch = (provider: NavigatorProvider) =>
    provider.isBestMatch ?? bestMatchProviderIds?.has(provider.id) ?? false;
  const [internalIndex, setInternalIndex] = useState(0);
  const [showAlts, setShowAlts] = useState(false);
  // Provider id that just got promoted — drives the confirmation ring pulse.
  const [lastSwapId, setLastSwapId] = useState<string | null>(null);
  // A swap POST is still in flight — Select actions are disabled meanwhile.
  const [swapPending, setSwapPending] = useState(false);

  const isControlled = activeIndex !== undefined;
  const currentIndex = isControlled ? activeIndex : internalIndex;

  const { categories } = viewModel;
  const { tabs, additional, conciergeOnly } = splitCategories(categories, { isMatching });
  // Only the tab strip drives this index, so clamp to it: a controlled caller
  // passing a larger index would otherwise render a panel no tab is showing as
  // selected.
  const tabIndex = Math.min(currentIndex, Math.max(tabs.length - 1, 0));
  const activeCategory = tabs[tabIndex];
  const providers = activeCategory?.providers ?? [];
  const topProvider = featuredProviderFor(providers);
  const altProviders = providers.filter((provider) => provider !== topProvider);
  // Chip semantics: selection == rank 1 (or unknown rank) → amber Top Match;
  // swapped selection → sage "Your selection" (top-match tag moves to the alt).
  const featuredIsTopMatch = topProvider ? (topProvider.rankOrder ?? 1) === 1 : false;

  const changeCategory = (index: number) => {
    if (isControlled) {
      onActiveIndexChange?.(index);
    } else {
      setInternalIndex(index);
    }
    setShowAlts(false);
  };

  const toggleAlts = () => {
    const next = !showAlts;
    setShowAlts(next);
    if (activeCategory) onAltsToggle?.(next, activeCategory);
  };

  const handleSelect = (provider: NavigatorProvider) => {
    if (!onSelectProvider || !activeCategory || swapPending) return;
    setLastSwapId(provider.id);
    const result = onSelectProvider(provider, activeCategory);
    // Promise-returning handlers gate further clicks until the swap settles.
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      setSwapPending(true);
      void Promise.resolve(result).finally(() => setSwapPending(false));
    }
  };

  const renderTop = (provider: NavigatorProvider) =>
    renderProvider ? (
      renderProvider(provider, {
        category: activeCategory,
        index: 0,
        isTopMatch: featuredIsTopMatch,
        isSelected: true,
        isBestMatch: isBestMatch(provider),
      })
    ) : (
      <NavigatorProviderCard
        provider={provider}
        featured
        isTopMatch={featuredIsTopMatch}
        isSelection={!featuredIsTopMatch}
        isBestMatch={isBestMatch(provider)}
        topMatchLabel={copy.topMatchChip}
        selectionLabel={copy.selectionChip}
        whyThisMatchLabel={copy.whyThisMatchLabel}
        actions={renderProviderActions?.(provider, activeCategory)}
      />
    );

  const renderAlt = (provider: NavigatorProvider, index: number) =>
    renderProvider ? (
      renderProvider(provider, {
        category: activeCategory,
        index: index + 1,
        isTopMatch: false,
        isSelected: false,
        isBestMatch: isBestMatch(provider),
        onSelect: onSelectProvider ? () => handleSelect(provider) : undefined,
        selectDisabled: swapPending,
      })
    ) : (
      // Alternates render as compact rows; "Select" promotes one to the
      // featured slot (single-select swap) when a handler is wired.
      <NavigatorAltRow
        provider={provider}
        isBestMatch={isBestMatch(provider)}
        onSelect={onSelectProvider ? handleSelect : undefined}
        selectDisabled={swapPending}
        selectLabel={copy.selectButtonLabel}
        topMatchLabel={copy.topMatchChip}
        whyThisMatchLabel={copy.whyThisMatchLabel}
      />
    );

  return (
    // Staggered reveal: back-link/title → care-stage card → tabs → providers.
    // Enter-only animations (no exit) keep tests and screen readers unaffected.
    <MotionConfig reducedMotion="user">
      <motion.section
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="space-y-6"
      >
        {topSlot && <motion.div variants={fadeInUp}>{topSlot}</motion.div>}

        <motion.div variants={fadeInUp}>
          {titleSlot ?? (
            <h1 className="font-termina text-2xl font-medium text-text-primary">{viewModel.planTitle}</h1>
          )}
        </motion.div>

        <motion.div variants={fadeInUp}>
          <NavigatorCareStageCard
            eyebrow={copy.careStageEyebrow}
            careStageLabel={viewModel.careStageLabel}
            summaryBullets={viewModel.summaryBullets}
            fallbackLine={copy.careStageFallbackLine}
            hideLabel={viewModel.careStageLabel.trim() === viewModel.planTitle.trim()}
          />
        </motion.div>

        {beforeTabsSlot && <motion.div variants={fadeInUp}>{beforeTabsSlot}</motion.div>}

        {headerActions && <motion.div variants={fadeInUp}>{headerActions}</motion.div>}

        {categories.length === 0 ? (
          <motion.div
            variants={fadeInUp}
            className="rounded-2xl border border-dashed border-border-subtle bg-card-subtle p-8 text-center"
          >
            <p className="text-sm text-text-secondary">{copy.emptyCategories}</p>
          </motion.div>
        ) : (
          <>
            <motion.div variants={fadeInUp}>
              <NavigatorCategoryTabs
                isMatching={isMatching}
                categories={tabs}
                activeIndex={tabIndex}
                onChange={changeCategory}
                bestMatchProviderIds={bestMatchProviderIds}
              />
            </motion.div>

            {activeCategory && (
              // Keyed by category so switching tabs softly fades the new panel in.
              <motion.div
                key={activeCategory.id}
                variants={fadeIn}
                initial="initial"
                animate="animate"
                className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]"
              >
                <LayoutGroup id={activeCategory.id}>
                  <div className="space-y-3">
                    <NavigatorSpecialtyIntro category={activeCategory} />

                    {!topProvider && isMatching ? (
                      // Search still pending for this specialty — the concierge
                      // hand-off would claim a result we do not have yet.
                      <div className="flex items-center gap-2 rounded-2xl border border-dashed border-border-subtle bg-card-subtle px-4 py-6 text-sm text-text-secondary">
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                        Finding providers…
                      </div>
                    ) : !topProvider ? (
                      <NavigatorConciergeEmptyState
                        copy={copy}
                        category={activeCategory}
                        onConciergeRequest={onConciergeRequest}
                      />
                    ) : (
                      // layoutId lets a promoted alt morph up into this slot
                      // (and the demoted card settle into the list below).
                      <motion.div
                        key={topProvider.id}
                        layoutId={topProvider.id}
                        layout
                        transition={{ duration: 0.38, ease: warmEase }}
                        className="rounded-2xl"
                        animate={lastSwapId === topProvider.id ? swapPulse : undefined}
                        onAnimationComplete={() => {
                          if (lastSwapId === topProvider.id) setLastSwapId(null);
                        }}
                      >
                        {renderTop(topProvider)}
                      </motion.div>
                    )}

                    <NavigatorAltsSection
                      providers={altProviders}
                      categoryName={activeCategory.name}
                      open={showAlts}
                      onToggle={toggleAlts}
                      renderAlt={renderAlt}
                    />
                  </div>
                </LayoutGroup>

                {renderKeyResources ? (
                  renderKeyResources(activeCategory)
                ) : (
                  <NavigatorKeyResources
                    title={copy.keyResourcesTitle}
                    resources={[...activeCategory.resources, ...viewModel.careStageResources]}
                  />
                )}
              </motion.div>
            )}

            {(additional.length > 0 || conciergeOnly.length > 0) && (
              <motion.div variants={fadeInUp} className="border-t border-border-subtle pt-8">
                {/* ponytail: the two headings below are inline and role-neutral.
                    Ceiling — the FP surface says "your client" elsewhere on this
                    screen, so this reads generic to advisors. Upgrade path: move
                    both strings into navigatorResultsCopy alongside the concierge
                    copy that already varies by role. */}
                <p className="font-termina text-xs font-bold uppercase tracking-wide text-sage">
                  Build out the care team
                </p>
                <p className="mt-2 text-sm text-text-secondary">
                  Beyond the core recommendations, these specialties often matter at this stage.
                </p>

                {/* Concierge-only specialties (search finished with no match) ride
                    in the same carousel, each offering the Care Concierge in its
                    own card — no separate dead-end block. */}
                <NavigatorAdditionalProviders
                  categories={[...additional, ...conciergeOnly]}
                  renderProviderActions={renderProviderActions}
                  isMatching={isMatching}
                  onConciergeRequest={onConciergeRequest}
                  conciergeCtaLabel={copy.conciergeCtaLabel}
                />
              </motion.div>
            )}
          </>
        )}

        {footerSlot && <motion.div variants={fadeIn}>{footerSlot}</motion.div>}
      </motion.section>
    </MotionConfig>
  );
};
